import React, { useState, useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography,Box } from "@mui/material";

import { format } from "date-fns";

// Assuming these helpers are passed down from the parent
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TOTAL_COLUMNS = 8; 

// The total columns for the detail rows (must match the header order)
const TOTAL_VALUE_KEYS = ['AMOUNT', 'VATAMT', 'COMMISSION', 'AMOUNT_EXCLVAT'];
const NUM_VALUE_COLUMNS = TOTAL_VALUE_KEYS.length;

// Note: The grandColumnTotals prop is now ignored/removed as totals are calculated dynamically.
const SalesTableGrouped = ({ rows, loading, groupSalesThreeLevels, loadNextPage, hasMore }) => {


    const tableContainerRef = useRef(null);
    const [collapsedState, setCollapsedState] = useState({});

    // Helper function for safe date formatting
    const safeFormatDate = (dateValue) => {
        try {
            const date = new Date(dateValue);
            // Check if the date is a valid object and not NaN (Invalid Date)
            if (date instanceof Date && !isNaN(date.getTime())) {
                return format(date, "dd-MM-yyyy");
            }
        } catch (e) {
            // Log error but return the raw value or a dash instead of crashing
            console.error("Error formatting date:", e);
        }
        return dateValue || '-'; // Return raw value or a dash if invalid
    };

    // 🚨 2. Define the scroll detection logic
    const handleTableScroll = () => {

        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };

    const toggleCollapse = (key) => {
        setCollapsedState(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // --- Core Logic to determine visibility and filter rows ---
    const isRowVisible = (row) => {
        if (!row.DEPTNO) return true;
        const deptKey = row.DEPTNO.trim();

        if (row.isGroupHeader) {
            return true;
        }
        
        // Rows below Level 1
        if (collapsedState[deptKey]) return false;

        // Rows below Level 2 (Type)
        if (row.isSubHeader || row.CASHCRED || !row.INVNO) {
            if (row.TYPE) {
                const typeKey = `${deptKey}-${row.TYPE.trim()}`;
                if (row.isSubHeader) return true;

                // Rows below Level 3 (CASHCRED)
                if (collapsedState[typeKey]) return false;

                if (row.CASHCRED || !row.INVNO) {
                    if (row.CASHCRED) {
                        const cashCredKey = `${typeKey}-${row.CASHCRED.trim()}`;
                        if (row.isSubSubHeader) return true;

                        // Detail row is only hidden if parent CASHCRED is collapsed
                        if (collapsedState[cashCredKey]) return false;
                    }
                }
            }
        }
        return true;
    };


    // 1. Calculate the currently visible rows using useMemo
    const visibleRows = useMemo(() => {
        return rows.filter(isRowVisible);
    }, [rows, collapsedState]);

    // 2. Calculate the grand total across ALL detail rows (transactional rows)
    // This total is static, regardless of the collapse state.
    const overallGrandTotals = useMemo(() => {
        const detailRows = rows.filter(row => 
            !row.isGroupHeader && !row.isSubHeader && !row.isSubSubHeader
        );

        const totals = {};
        
        for (const key of TOTAL_VALUE_KEYS) {
            totals[key] = detailRows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }

        return totals;
    }, [rows]);


    // Define the mapping from header keys to assumed group total keys
    const GROUP_TOTAL_MAPPING = [
        { headerKey: 'AMOUNT', totalKey: 'totalAmount' },
        { headerKey: 'VATAMT', totalKey: 'totalVATAMT' },
        { headerKey: 'COMMISSION', totalKey: 'totalCommission' },
        { headerKey: 'AMOUNT_EXCLVAT', totalKey: 'totalAmountExclVAT' },
    ];

    // Helper function to render the four total cells for group headers
    const renderGroupTotalCells = (row, weight) => {
        return GROUP_TOTAL_MAPPING.map(({ headerKey, totalKey }) => (
            <TableCell key={headerKey} align="right" sx={{ fontWeight: weight }}>
                {/* We assume the group utility provided these total fields on the row object */}
                {formatNumber(row[totalKey] || 0)}
            </TableCell>
        ));
    };


    const renderRow = (row, index) => {
        // We now iterate over `visibleRows` so we don't need the visibility check here.
        // We use the original index as key for stability.

        const isGroupHeader = row.isGroupHeader;
        const isSubHeader = row.isSubHeader;
        const isSubSubHeader = row.isSubSubHeader;
        const isDetailRow = !isGroupHeader && !isSubHeader && !isSubSubHeader;

        const deptKey = row.DEPTNO ? row.DEPTNO.trim() : null;
        const typeKey = row.TYPE && deptKey ? `${deptKey}-${row.TYPE.trim()}` : null;
        const cashCredKey = row.CASHCRED && typeKey ? `${typeKey}-${row.CASHCRED.trim()}` : null;
        
        // The span for header rows is TOTAL_COLUMNS - NUM_VALUE_COLUMNS (8 - 4 = 4)
        const headerColSpan = TOTAL_COLUMNS - NUM_VALUE_COLUMNS;


        // 1. Group Header (Level 1)
        if (isGroupHeader) {
            const isCollapsed = collapsedState[deptKey];
            return (
                <TableRow key={`dept-header-${index}`} onClick={() => toggleCollapse(deptKey)} sx={{ cursor: 'pointer', backgroundColor: '#e0f7fa' }}>
                    <TableCell colSpan={headerColSpan} style={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`Department Total: ${row.DEPTNO.trim()}`}
                    </TableCell>
                    {/* Render all four totals */}
                    {renderGroupTotalCells(row, 'bold')}
                </TableRow>
            );
        }

        // 2. Type Sub-Header (Level 2)
        if (isSubHeader) {
            const isCollapsed = collapsedState[typeKey];
            return (
                <TableRow key={`type-header-${index}`} onClick={() => toggleCollapse(typeKey)} sx={{ cursor: 'pointer', backgroundColor: '#f0f4c3' }}>
                    <TableCell colSpan={headerColSpan} style={{ fontWeight: '600', padding: '6px 16px', paddingLeft: '30px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`Type Sub-Total: ${row.TYPE.trim()}`}
                    </TableCell>
                    {/* Render all four totals */}
                    {renderGroupTotalCells(row, '600')}
                </TableRow>
            );
        }

        // 3. CASHCRED Sub-Sub-Header (Level 3)
        if (isSubSubHeader) {
            const isCollapsed = collapsedState[cashCredKey];
            return (
                <TableRow key={`cashcred-header-${index}`} onClick={() => toggleCollapse(cashCredKey)} sx={{ cursor: 'pointer', backgroundColor: '#fff8e1' }}>
                    <TableCell colSpan={headerColSpan} style={{ fontWeight: '500', padding: '4px 16px', paddingLeft: '60px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`CASH/CREDIT Sub-Total: ${row.CASHCRED.trim()}`}
                    </TableCell>
                    {/* Render all four totals */}
                    {renderGroupTotalCells(row, '500')}
                </TableRow>
            );
        }

        // 4. Detail Row (Transactional) - 8 columns
        if (isDetailRow) {
            return (
                <TableRow key={`detail-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>{row.INVNO}</TableCell>
                    <TableCell>{format(new Date( row.INV_DATE || row.DATE ), "dd-MM-yyyy") }</TableCell>
                    <TableCell>{row.CUSTOMER}</TableCell>
                    {/* FIX: Use bracket notation for keys with spaces, like "SALES MAN" */}
                    <TableCell>{row["SALES MAN"] || row.SALESPERSON}</TableCell>
                    {/* The 4 value columns (AMOUNT, VATAMT, COMMISSION, AMOUNT_EXCLVAT) */}
                    {TOTAL_VALUE_KEYS.map(key => (
                         <TableCell key={key} align="right">{formatNumber(row[key])}</TableCell>
                    ))}
                </TableRow>
            );
        }
        return null;
    };

    // --- NEW: Render the overall Grand Total row (static, based on all data) ---
    const renderOverallGrandTotal = () => {
        // We now rely on the static overallGrandTotals, which is always calculated over the full data set.
        if (rows.length === 0) return null;

        // The span covers all non-value columns (8 - 4 = 4)
        const labelColSpan = TOTAL_COLUMNS - NUM_VALUE_COLUMNS; 
        
        return (
            <TableRow sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                {/* 1. GRAND TOTAL Label (Static) */}
                <TableCell colSpan={labelColSpan} align="right"
                    sx={{ fontWeight: 'bolder', fontSize: '1.2em', padding: '8px 16px' }}>
                    GRAND TOTAL:
                </TableCell>
                
                {/* 2. Total Values for the 4 Value Columns */}
                {TOTAL_VALUE_KEYS.map(key => (
                    <TableCell key={key} align="right"
                        sx={{ fontWeight: 'bolder', fontSize: '1.2em' }}>
                        {formatNumber(overallGrandTotals[key])}
                    </TableCell>
                ))}
            </TableRow>
        );
    };


    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll} 
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}>
            <Table stickyHeader aria-label="grouped sales report">
                <TableHead>
                    <TableRow>
                        <TableCell style={{ minWidth: 100 }}>INV NO</TableCell>
                        <TableCell style={{ minWidth: 120 }}>INV DATE</TableCell>
                        <TableCell style={{ minWidth: 200 }}>CUSTOMER</TableCell>
                        <TableCell style={{ minWidth: 120 }}>SALES MAN</TableCell>
                        <TableCell align="right" style={{ minWidth: 120 }}>AMOUNT</TableCell>
                        <TableCell align="right" style={{ minWidth: 120 }}>VATAMT</TableCell>
                        <TableCell align="right" style={{ minWidth: 120 }}>COMMISSION</TableCell>
                        <TableCell align="right" style={{ minWidth: 120 }}>AMOUNT EXCLVAT</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {visibleRows.map((row, index) => renderRow(row, index))}

                    {/* NEW: Overall Grand Total Row (static) */}
                    {renderOverallGrandTotal()}

                    {/* Load More Indicator */}
                    {hasMore && (
                        <TableRow>
                            <TableCell colSpan={TOTAL_COLUMNS} align="center">
                                <Box sx={{ py: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="caption" display="block">
                                        Loading more data...
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && rows.length === 0 && (
                        <TableRow><TableCell colSpan={TOTAL_COLUMNS} align="center"><Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography></TableCell></TableRow>
                    )}
                </TableBody>

            </Table>

            {/* Loading Message */}
            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}
        </TableContainer>
    );
};

export default SalesTableGrouped;